import { MediaGalleryContent } from './MediaGallery.js';
import { getHash } from "../../utils/utils.js"
import { PopupRenderer } from '../../map/popup/PopupRenderer.js';
import { TimeConversion } from './HelperTools.js';
/**
 * Request related helper methods for the gallery 
 * 
 * @author ckraemme <ckraemme@awi.de>
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.ui.media
 * @private
 */
export class RequestDataService {

    /**
     * Request multiple JSON objects via url list
     * @param {Object.Array} urlList - list of urls
     * @return {Promise} Promises to the JSON requests
    */
    static requestMultipleJSONs(urlList) {
        let promises = []
        urlList.forEach((url) => {
            promises.push(RequestDataService.requestJSON(url));
        });
        return Promise.all(promises)
    }

    /**
     * Create a request to a JSON file and return its data as an object via promise
     * @param {string} url - URL to the JSON file 
     * @return {Promise} Promise containing json data when resolved
    */
    static requestJSON(url) {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then(response => response.json())
                .then(json => resolve(json), (error) => reject(error))
        })
    }

    /**
     * Create WFS request via cql filter 
     * @param {string} baseUrl - base URL of the web feature service
     * @param {string} layer - layer name for wfs requests 
     * @param {string} cqlFilter - cql filter phrase
     * @return {Promise} Promise to the WFS request
    */
    static createWFSRequest(baseUrl, layer, cqlFilter) {
        const queryString = `service=WFS&version=2.0.0&request=GetFeature&typeName=${layer}&outputFormat=application/json&SrsName=EPSG:4326`;
        const url = `${baseUrl}?${queryString}&cql_filter=${encodeURI(cqlFilter)}`;
        return url
    }

    /**
     * Create requests to template files
     * @param {Object.Array} pathList - list of paths
     * @return {Promise} Promises to the template requests
    */
    static createTemplateRequests(pathList) {
        let promises = []
        pathList.forEach((path) => {
            promises.push(PopupRenderer.fetchTemplate(path));
        });
        return Promise.all(promises)
    }
}


/**
 * WFS related helper methods for preparing the gallery list items 
 * 
 * @author ckraemme <ckraemme@awi.de>
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.ui.media
 * @private
 */
export class WFSDataService {

    /**
     * Merge features from different layers and sort them
     * @param {Array.<Object>} featureCollectionList list containing WFS feature collections as items
     * @param {String} sortByKey - feature property key to sort groups
     * @returns {Array.<Object>} merged and sorted list
     */
    static mergeWFSResponses(featureCollectionList, sortByKey = 'date_time') {

        if (featureCollectionList.length <= 1)
            return featureCollectionList[0];

        let mergedFeatureInfo = {};
        for (let i = 0; i < featureCollectionList.length; i++) {
            const featureInfo = featureCollectionList[i];
            Object.keys(featureInfo).forEach((key) => {
                const item = featureInfo[key];

                if (typeof mergedFeatureInfo[key] == 'undefined')
                    mergedFeatureInfo[key] = item;
                else {
                    const type = Object.prototype.toString.call(item)
                    // skip: [object String], [object Null], [object Undefined], [object Object], etc.
                    // hence for this fields the first occurrence of a defined value is kept
                    if (type === "[object Number]")
                        mergedFeatureInfo[key] += item
                    else if (type === '[object Array]') {
                        let deepCopyVariable = [];
                        deepCopyVariable.push(...mergedFeatureInfo[key]);
                        deepCopyVariable.push(...item);
                        mergedFeatureInfo[key] = deepCopyVariable;
                    }
                }
            })
        }

        mergedFeatureInfo.features.sort(function (a, b) {
            const sortValueA = a["properties#harmonized"][sortByKey];
            const sortValueB = b["properties#harmonized"][sortByKey];
            return ((sortValueA < sortValueB) ? -1 : (sortValueA > sortValueB) ? 1 : 0);
        });

        return mergedFeatureInfo;
    }

    /**
     * Group wfs feature infos and sort them afterwards
     * 
     * @param {Array.<Object>} featureList - list containing WFS feature infos as items
     * @param {String} groupByKey - feature property key to create groups
     * @param {String} sortByKey - feature property key to sort groups
     * @returns {Array.<Object>} grouped feature info list
     */
    static groupDuplicateFeatures(featureList, groupByKey = 'persistent_identifier', sortByKey = 'date_time') {

        const groupedFeatureInfoDict = featureList.reduce((accumulator, currentValue) => {
            accumulator[currentValue["properties#harmonized"][groupByKey]] = accumulator[currentValue["properties#harmonized"][groupByKey]] || [];
            accumulator[currentValue["properties#harmonized"][groupByKey]].push(currentValue);
            return accumulator;
        }, Object.create(null));

        let groupedFeatureList = Object.keys(groupedFeatureInfoDict).map((key) => groupedFeatureInfoDict[key]);

        groupedFeatureList.sort(function (a, b) {
            return ((a[0]["properties#harmonized"][sortByKey] < b[0]["properties#harmonized"][sortByKey]) ? -1 : ((a[0]["properties#harmonized"][sortByKey] == b[0]["properties#harmonized"][sortByKey]) ? 0 : 1));
        });

        const unpackedFeatureList = groupedFeatureList.map((item) => { return item.length == 1 ? item[0] : item });
        return unpackedFeatureList
    }

    /**
     * Map all feature infos in feature collections based on a mapping scheme. Rename the dictionary keys in all the feature info properties of a feature collection. Observe: Layer-based order of featureCollectionListWFS and mappingDictionaryList must be identical
     * 
     * @param {Object} featureCollectionListWFS - list with WFS feature collections
     * @param {Object} mappingDictionaryList - dictionary with layer based mapping scheme
     * @returns {Object} list with mapped feature collection
     */
    static mapFeatureCollectionList(featureCollectionListWFS, mappingDictionaryList) {
        let featureCollectionList = []
        featureCollectionListWFS.forEach((featureCollectionWFS, index) => {
            const featureCollection = WFSDataService.mapFeatureCollection(featureCollectionWFS, mappingDictionaryList[index]);
            featureCollectionList.push(featureCollection)
        })
        return featureCollectionList
    }

    /**
     * Based on a mapping scheme, rename the dictionary keys in all the feature info properties of a feature collection
     * 
     * @param {Object} featureCollectionWFS - WFS feature collection
     * @param {Object} mappingDict - dictionary with layer based mapping scheme
     * @returns {Object} mapped feature collection
     */
    static mapFeatureCollection(featureCollectionWFS, mappingDict) {
        let featureCollection = structuredClone(featureCollectionWFS);

        featureCollection.features.forEach((featureInfoObjectWFS, index1) => {
            if (Array.isArray(featureInfoObjectWFS)) {
                featureInfoObjectWFS.forEach((featureInfoWFS, index2) => {
                    featureCollection.features[index1][index2] = WFSDataService.mapFeatureInfo(featureInfoWFS, mappingDict);
                })
            }
            else {
                let featureInfoWFS = featureInfoObjectWFS;
                featureCollection.features[index1] = WFSDataService.mapFeatureInfo(featureInfoWFS, mappingDict);
            }
        })
        return featureCollection
    }

    /**
     * Based on a mapping scheme, rename the keys in the feature info property
     * 
     * @param {Object} featureInfo - WFS feature info
     * @param {Object} mappingDict - dictionary with mapping scheme: {'mapping': {'properties': {new_key: old_key}}}
     * @returns {Object} mapped feature info
     */
    static mapFeatureInfo(featureInfo, mappingDict) {

        const defaultScheme_v1_2 = GalleryMappingScheme.getDefault_v1_2();
        const jsonScheme_v1_2 = mappingDict['mapping'];
        const scheme = GalleryMappingScheme.merge(defaultScheme_v1_2, jsonScheme_v1_2);

        Object.keys(scheme).forEach((key) => {
            featureInfo[key + '#harmonized'] ??= {};
            Object.keys(scheme[key]).forEach((subKey) => {
                const mapKey = scheme[key][subKey];
                if (Array.isArray(mapKey)) {
                    mapKey.forEach((subKeyFromMapItem) => {
                        featureInfo[key + '#harmonized'][subKey] = featureInfo[key + '#harmonized'][subKey] || [];
                        featureInfo[key + '#harmonized'][subKey].push(featureInfo[key][subKeyFromMapItem]);
                    })
                }
                else
                    featureInfo[key + '#harmonized'][subKey] = featureInfo[key][mapKey];
            })
        })
        return featureInfo
    }
}


/**
 * Gallery mapping scheme related class
 * 
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class GalleryMappingScheme {
    /**
     * Deep merge of two gallery mapping schemes
     * @param {Object} defaultScheme 
     * @param {Object} referenceScheme
     * @returns {Object} merged scheme
     */
    static merge(defaultScheme, referenceScheme) {
        return _.merge(defaultScheme, referenceScheme)
    }

    /**
     * Get the default gallery mapping scheme of version 1.2
     * @returns {Object} mapping scheme version 1.2
     */
    static getDefault_v1_2() {
        const defaultMappingSchemeKeys_v1_2 = ["event", "image-acquisition", "image-marine-zone", "image-deployment", "image-navigation", "image-illumination", "image-scale-reference", "image-quality", "image-pixel-magnitude", "image-spectral-resolution", "image-capture-mode", "image-license", "depth", "citation", "pi_name", "mime_type", "photo_src", "photo_srcset", "video_src", "photo_width", "photo_height", "video_width", "video_height", "thumbnail_src", "cover_src", "cover_srcset", "poster_src", "duration", "persistent_identifier", "name", "date_time"];

        let tmp = {}
        defaultMappingSchemeKeys_v1_2.forEach(item => { tmp[item] = item })

        let defaultScheme_v1_2 = { 'properties': tmp }
        return defaultScheme_v1_2
    }
}


/**
 * WFS related helper methods for preparing media items
 * 
 * @author ckraemme <ckraemme@awi.de>
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.ui.media
 * @private
 */
export class MediaDataService {
    /**
     * WFS data is based on user input. In order to use them generically, some vocabularies need to be standardized
     * @param {Array.<Object>} featureList list containing WFS feature infos as items
     * @returns {Array.<Object>} featureList
     */
    static unifyData(featureList) {
        for (let i = 0; i < featureList.length; ++i) {
            const item = featureList[i];
            MediaDataService._validateMediaType(item);
            if (!item["properties#harmonized"]["persistent_identifier"])
                item["properties#harmonized"]["persistent_identifier"] = MediaDataService._getMediaHash(item);
            if (item["properties#harmonized"]["duration"])
                item["properties#harmonized"]["duration"] = TimeConversion.time2IsoDayTime(item["properties#harmonized"]["duration"]);
        }
        return featureList;
    }

    /**
     * Validate media and acquisition type
     * @param {string} item - wfs feature response item
     */
    static _validateMediaType(item) {
        if (item["properties#harmonized"]["mime_type"] == null && item["properties#harmonized"]["image-acquisition"] == null) {
            //'The "octet-stream" subtype is used to indicate that a body contains arbitrary binary data' [RFC 2046, section 4.5.1].
            if (item["properties#harmonized"].photo_src != null)
                item["properties#harmonized"]["mime_type"] = "image/octet-stream";
            else if (item["properties#harmonized"].video_src != null)
                item["properties#harmonized"]["mime_type"] = "video/octet-stream";
            else {
                const fallback = "image/octet-stream";
                item["properties#harmonized"]["mime_type"] = fallback;
                console.warn(`Using fallback media type ${fallback}: No media source found`)
            }
            console.warn('Guessing media type: ' + item["properties#harmonized"]["mime_type"])
        }
        if (item["properties#harmonized"]["mime_type"] == null) {
            switch (item["properties#harmonized"]["image-acquisition"]) {
                case 'photo':
                    item["properties#harmonized"]["mime_type"] = "image/octet-stream";
                    //console.debug(`Guessing media type: ${item["properties#harmonized"]["mime_type"]}`);
                    break
                case 'video':
                    item["properties#harmonized"]["mime_type"] = "video/octet-stream";
                    //console.debug(`Guessing media type: ${item["properties#harmonized"]["mime_type"]}`);
                    break
                default:
                    item["properties#harmonized"]["mime_type"] = "";
                    console.warn(`Can not set media type based on acquisition: ${item["properties#harmonized"]["image-acquisition"]}`);
            }
        }
        if (item["properties#harmonized"]["image-acquisition"] == null) {
            switch (item["properties#harmonized"]["mime_type"].split('/')[0]) {
                case 'image':
                    item["properties#harmonized"]["image-acquisition"] = "photo";
                    //console.debug(`Guessing acquisition value: ${item["properties#harmonized"]["image-acquisition"]}`);
                    break
                case 'video':
                    item["properties#harmonized"]["image-acquisition"] = "video";
                    //console.debug(`Guessing acquisition value: ${item["properties#harmonized"]["image-acquisition"]}`);
                    break
                default:
                    item["properties#harmonized"]["image-acquisition"] = "";
                    console.warn(`Can not set acquisition value based on type: ${item["properties#harmonized"]["mime_type"]}`);
            }
        }
        // RFC 2045: "The type, subtype, and parameter names are not case sensitive" -> normalization required
        item["properties#harmonized"]["mime_type"] = item["properties#harmonized"]["mime_type"].toLowerCase();
    }


    /**
     * Generate a hash value for a wfs feature response item. The hash consists of a sequence of metadata values that clearly describes the data item
     * @param {string} item - wfs feature response item
     * @return {string} hash
     */
    static _getMediaHash(item) {
        const seed = 0;
        const separator = '|';
        const fallback = [item["properties#harmonized"]["date_time"], item.geometry.coordinates.join(separator)];
        const hashList = (item["properties#harmonized"]["name"]) ? [item["properties#harmonized"]["name"]] : fallback;
        return getHash(hashList, separator, seed);
    }

    /**
     * Returns a default fallback media dimension. Can be used for width and height. Prints a warning when this method is used.
     */
    static getFallbackDimension() {
        console.warn('Use of fallback media dimensions. This may result in warped media content.');
        return 1000;
    }
}


/**
 * Class related to media file paths. Requires project related folder structure
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class MediaFilePaths {

    /**
     * Return a url which is structured as follows: $fileBasePath/$resolution/$filename
     * @param {string} fileBasePath - i.e., "https://.../../"
     * @param {string} resolution - i.e., "720"
     * @param {string} filename - i.e., "photo.webp"
     * @return {string} url -i.e., "https://.../../720/photo.webp"
     */
    static getSrc(fileBasePath, resolution, filename) {
        const urlParts = [fileBasePath, resolution, filename];
        const url = urlParts.join('/').replace(/[\/]+/g, '/').replace(/^(.+):\//, '$1://');
        return url
    }

    /**
     * Return the image srcset attribute value for an image encoded with multiple resolutions
     * @param {string} fileBasePath - i.e., "https://.../../"
     * @param {string} filename - i.e., "photo.webp"
     * @param {Array} resolutionSet - i.e., ["720","1080"]
     * @param {Array} widthDescriptorSet - i.e., ["1280w", "1920w"]
     * @return {string} srcset - i.e., "https://.../../720/photo.webp 1280w, https://.../../1080/photo.webp 1920w"
     */
    static getSrcSet(fileBasePath, filename, resolutionSet, widthDescriptorSet) {
        var srcsetSet = []
        resolutionSet.forEach((resolution, index) => {

            const url = MediaFilePaths.getSrc(fileBasePath, resolution, filename);
            const widthDescriptor = widthDescriptorSet[index];
            srcsetSet.push(`${url} ${widthDescriptor}`)

        })
        return srcsetSet.join(', ')
    }
}


/**
 * Class representing the stack of items in the media gallery
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class MediaDataStack {
    /**
     * Describes default properties of the stack, thus represents empty stack
     * @param {string/HTMLElement} target - target HTML element, serves as anchor for the media gallery
     * @param {Array.<Object>} featureList - list containing WFS feature infos as items
     */
    constructor(target, featureList) {
        this.target = target;
        this.featureList = featureList
        this.itemsInGallery = 0;
        this.itemsInGalleryMaximum = this.featureList.length;
    }

    /**
    * Insert multiple media items (photos and / or videos) into the gallery
    * @param {Number} number - number of items to put next on the media data stack
    */
    insertIntoGallery(number) {
        const oldItemsInGallery = this.itemsInGallery;
        const newItemsInGallery = Math.min(oldItemsInGallery + number, this.itemsInGalleryMaximum);
        for (let i = oldItemsInGallery; i < newItemsInGallery; i++) {
            MediaGalleryContent.addContent(this.target, this.featureList[i]);
        }
        //console.log(`${newItemsInGallery - oldItemsInGallery} media files included`)

        $(this.target).justifiedGallery('norewind')
        this.itemsInGallery = newItemsInGallery
    }
}

/**
 * Class for loading gallery media items in groups
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class MediaBatchLoader {
    /**
     * Describes default properties that extend the MediaDataStack
     * @param {Class} mediaDataStack - dependency injection of the MediaDataStack class
     * @param {Number} groupSize - number of items to put in single groups on the media data stack
     * @param {Number} totalSize - number of items to put in total on the media data stack
     */
    constructor(mediaDataStack, groupSize, totalSize) {
        this.mediaDataStack = mediaDataStack;
        this.groupSize = groupSize;
        this.totalSize = totalSize;
        this.totalSizeLimit = Math.min(this.mediaDataStack.itemsInGallery + this.totalSize, this.mediaDataStack.itemsInGalleryMaximum)
    }
    /**
     * Load the next group of media items
     */
    loadNextBatch() {
        if (this.mediaDataStack.itemsInGallery < this.totalSizeLimit) {
            this.mediaDataStack.insertIntoGallery(this.groupSize);
        }
        else {
            //console.log('Batch loading done')
        }
    }
    /**
     * Load all groups of media items sequentially
     */
    loadAllBatches() {
        for (let i = this.mediaDataStack.itemsInGallery; i < this.totalSizeLimit; i += this.groupSize) {
            this.loadNextBatch()
        }
    }
}

/**
 * Class for loading gallery media items when user scrolls the media gallery
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class MediaScrollLoader {
    /**
     * Describes default properties that extend the MediaDataStack
     * @param {Class} mediaDataStack - dependency injection of the MediaDataStack class
     * @param {HTMLElement} scrollElement - element containing the vertical media gallery scrollbar
     * @param {HTMLElement} galleryElement - media gallery element
     */
    constructor(mediaDataStack, scrollElement, galleryElement) {
        this.mediaDataStack = mediaDataStack;
        this.scrollElement = scrollElement;
        this.galleryElement = galleryElement;
        this.isLoading = false;
    }

    /**
     * Put media gallery items into the MediaDataStack when the media gallery is scrolled down below a threshold height.
     * Observe: To prevent items from being inserted into the gallery even though the item insertion is already in process, there exists the variable MediaScrollLoader.isLoading. This must be set to false (outside of this method) as soon as item insertion is completed (i.e., media items become visible in the gallery), to allow further items to be inserted when scrolling
     * @param {Number} groupSize - number of items that should be loaded at once. Loading is repeated until the threshold is reached 
     * @param {Number} loadThreshold - percentage of the current media gallery height where further media should be loaded
     */
    loadViaScrolling(groupSize, loadThreshold) {
        const viewedGalleryInPercent = (this.scrollElement.scrollTop + window.innerHeight) / (this.galleryElement.offsetTop + this.galleryElement.offsetHeight) * 100
        //console.log(viewedGalleryInPercent);
        if (viewedGalleryInPercent > loadThreshold && this.mediaDataStack.itemsInGallery != this.mediaDataStack.itemsInGalleryMaximum) {
            if (this.isLoading === false) {
                this.isLoading = true
                this.mediaDataStack.insertIntoGallery(groupSize)
            }
            else {
                if (!this.interval)
                    this.interval = setInterval(function () { this.loadViaScrolling(groupSize, loadThreshold) }.bind(this), 200)
            }
        }
        else {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        }
    }
}

/**
 * Class for media gallery related data provision
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class MediaGalleryService {
    /**
     * Tries to match the given identifier to the 'persistent_identifier' value for each media item in the feature info list. 
     * If successful, return the position of the media item in the list as slide number
     * @param {Array.<Object>} featureList - list containing WFS feature infos as items
     * @param {string} identifier - string to identify media item, i.e., hash
     * @return {Number} number of slide
     */
    static getSharedSlideNumber(featureList, identifier) {
        let slideOnStart;
        if (identifier != null) {
            for (let index = 0; index < featureList.length; index++) {
                let feature = featureList[index];
                if (Array.isArray(feature)) feature = feature[0];
                if (feature["properties#harmonized"]['persistent_identifier'] == identifier) {
                    slideOnStart = index;
                    break;
                }
            }
            if (slideOnStart == null)
                console.log(`No item found which matched the identifier: ${identifier}`)
        }
        return slideOnStart
    }
}